import {
  INTERNAL_ORG_SLUG,
  organizationList,
  usageMetrics,
  userList,
  type AssignableRole,
  type PlatformRole,
  type UserSummary,
} from "@ir/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Pagination } from "@/components/ui/Pagination";
import { StatCard } from "@/components/ui/StatCard";
import { EditUserDialog, type EditUserPayload } from "@/features/admin/EditUserDialog";
import { InviteUserDialog, type InvitePayload } from "@/features/admin/InviteUserDialog";
import { UsersTable } from "@/features/admin/UsersTable";
import { ApiError, apiFetch, buildQuery } from "@/lib/api";
import { authClient } from "@/lib/authClient";
import { optionalProp } from "@/lib/optionalProp";

// redirect() devuelve un Response, no un Error: ver la nota equivalente en
// _auth.tsx. Se usa como función de aserción (con la opción `throw` del
// propio redirect, que lo lanza por dentro) para que TypeScript siga
// estrechando `user` tras el guard sin dejar ningún `throw` de un valor
// no-Error en el código propio.
function assertAdmin<T extends { role?: string | null | undefined }>(
  user: T | null | undefined,
): asserts user is T {
  if (user?.role !== "ir_admin") redirect({ to: "/insights", throw: true });
}

export const Route = createFileRoute("/_auth/admin/usuarios")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    const user = data?.user;
    assertAdmin(user);
    return { userId: user.id };
  },
  component: AdminUsersPage,
});

const PAGE_SIZE = 24;

function AdminUsersPage() {
  const { t } = useTranslation();
  const { userId } = Route.useRouteContext();
  const queryClient = useQueryClient();

  const [invitando, setInvitando] = useState(false);
  const [inviteError, setInviteError] = useState<{ code: string; message: string } | null>(null);
  const [editando, setEditando] = useState<UserSummary | null>(null);
  const [membresias, setMembresias] = useState<string[]>([]);
  const [errorMembresias, setErrorMembresias] = useState<string | null>(null);

  // Estado propio del diálogo "Editar usuario" (nombre, correo, rol): aparte
  // de `editando`/`membresias`, que son del diálogo de organizaciones.
  const [usuarioEnEdicion, setUsuarioEnEdicion] = useState<UserSummary | null>(null);
  const [errorEdicion, setErrorEdicion] = useState<{ code: string; message: string } | null>(
    null,
  );

  const [busqueda, setBusqueda] = useState("");
  const [rolFiltro, setRolFiltro] = useState<PlatformRole | "">("");
  const [pagina, setPagina] = useState(1);
  // Difiere la consulta para no lanzar una petición por cada tecla, igual
  // que el catálogo (_auth.insights.index.tsx).
  const deferredBusqueda = useDeferredValue(busqueda);

  const users = useQuery({
    queryKey: ["users", { q: deferredBusqueda, role: rolFiltro, page: pagina }],
    queryFn: () =>
      apiFetch(
        `/api/users${buildQuery({
          q: deferredBusqueda || undefined,
          role: rolFiltro || undefined,
          page: pagina,
          pageSize: PAGE_SIZE,
        })}`,
        { schema: userList },
      ),
  });

  const metrics = useQuery({
    queryKey: ["metrics"],
    queryFn: () => apiFetch("/api/metrics/usage", { schema: usageMetrics }),
  });

  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => apiFetch("/api/organizations", { schema: organizationList }),
    retry: false,
  });

  const opciones = (organizations.data?.items ?? []).map((o) => ({ id: o.id, label: o.name }));
  // Por slug, no por `kind`: ver la nota en INTERNAL_ORG_SLUG.
  const interna = organizations.data?.items.find((o) => o.slug === INTERNAL_ORG_SLUG);

  const toggleStatus = useMutation({
    mutationFn: ({ id, banned }: { id: string; banned: boolean }) =>
      apiFetch(`/api/users/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ banned }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
    onError: () => toast.error(t("common.error")),
  });

  const resendInvite = useMutation({
    mutationFn: (objetivo: UserSummary) =>
      apiFetch(`/api/users/${objetivo.id}/resend-invite`, { method: "POST" }),
    onSuccess: (_data, objetivo) =>
      toast.success(t("admin.resendInviteSent", { email: objetivo.email })),
    // El 409 de cuenta desactivada trae su propio mensaje, que dice qué hacer.
    onError: (fallo) =>
      toast.error(fallo instanceof ApiError ? fallo.message : t("common.error")),
  });

  const invite = useMutation({
    mutationFn: (payload: InvitePayload) =>
      apiFetch("/api/users/invite", { method: "POST", body: JSON.stringify(payload) }),
  });

  const setOrganizations = useMutation({
    mutationFn: ({ id, organizationIds }: { id: string; organizationIds: string[] }) =>
      apiFetch(`/api/users/${id}/organizations`, {
        method: "PUT",
        body: JSON.stringify({ organizationIds }),
      }),
  });

  const updateProfile = useMutation({
    mutationFn: ({ id, name, email }: { id: string; name: string; email: string }) =>
      apiFetch(`/api/users/${id}/profile`, {
        method: "PATCH",
        body: JSON.stringify({ name, email }),
      }),
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: AssignableRole }) =>
      apiFetch(`/api/users/${id}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
  });

  const invitar = async (payload: InvitePayload) => {
    setInviteError(null);
    try {
      await invite.mutateAsync(payload);
      setInvitando(false);
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (fallo) {
      // El 409 de correo repetido se pinta sobre el campo, no como error
      // genérico: es el único que la persona puede corregir sin ayuda. Y el
      // diálogo se queda abierto, para no perder lo escrito.
      if (fallo instanceof ApiError) {
        setInviteError({ code: fallo.code, message: fallo.message });
        return;
      }
      toast.error(t("common.error"));
    }
  };

  const guardarMembresias = async (objetivo: UserSummary) => {
    setErrorMembresias(null);
    try {
      await setOrganizations.mutateAsync({ id: objetivo.id, organizationIds: membresias });
      setEditando(null);
      // La columna de organizaciones sale del listado de usuarios, y el
      // acceso a los insights depende de estas membresías.
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["studies"] });
    } catch (fallo) {
      setErrorMembresias(fallo instanceof ApiError ? fallo.message : t("common.error"));
    }
  };

  const guardarUsuario = async (objetivo: UserSummary, payload: EditUserPayload) => {
    setErrorEdicion(null);
    try {
      await updateProfile.mutateAsync({
        id: objetivo.id,
        name: payload.name,
        email: payload.email,
      });
    } catch (fallo) {
      // Mismo criterio que `invitar`: el 409 de correo repetido se pinta
      // sobre el propio campo, y el diálogo se queda abierto.
      if (fallo instanceof ApiError) {
        setErrorEdicion({ code: fallo.code, message: fallo.message });
        return;
      }
      toast.error(t("common.error"));
      return;
    }

    // El perfil ya se guardó. Si además cambió el rol -y no es la propia
    // fila; el Select de EditUserDialog ya lo deja deshabilitado, pero la
    // comprobación no depende de eso-, se intenta aparte: un fallo aquí no
    // deshace el perfil, así que se avisa con un aviso genérico en vez de
    // reabrir el diálogo, igual que ya hace `toggleStatus.onError`.
    if (payload.role !== objetivo.role && objetivo.id !== userId) {
      try {
        await updateRole.mutateAsync({ id: objetivo.id, role: payload.role });
      } catch {
        toast.error(t("common.error"));
      }
    }

    setUsuarioEnEdicion(null);
    await queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  return (
    <>
      <PageHeader
        title={t("admin.usersTitle")}
        subtitle={t("admin.usersSubtitle")}
        actions={
          <>
            <div className="relative">
              <Search
                size={13}
                className="absolute top-1/2 left-2.5 -translate-y-1/2 text-ink4"
                aria-hidden
              />
              <input
                type="search"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
                placeholder={t("admin.searchUsers")}
                aria-label={t("admin.searchUsers")}
                className="h-9 w-[220px] rounded-[3px] border border-line bg-card pr-3 pl-8 text-[13px] placeholder:text-ink4 focus-visible:border-bur focus-visible:outline-none"
              />
            </div>
            <select
              value={rolFiltro}
              onChange={(e) => {
                // Comparación explícita, no `as`: mismo criterio que ya usa
                // InviteUserDialog para estrechar el rol de su propio select.
                const valor = e.target.value;
                setRolFiltro(
                  valor === "ir_admin" ||
                    valor === "ir_publisher" ||
                    valor === "ir_analyst" ||
                    valor === "client"
                    ? valor
                    : "",
                );
                setPagina(1);
              }}
              aria-label={t("admin.role")}
              className="h-9 rounded-[3px] border border-line bg-card px-2 text-[13px] text-ink2"
            >
              <option value="">{t("admin.allRoles")}</option>
              <option value="ir_admin">{t("admin.roleAdmin")}</option>
              <option value="ir_publisher">{t("admin.rolePublisher")}</option>
              <option value="ir_analyst">{t("admin.roleAnalyst")}</option>
              <option value="client">{t("admin.roleClient")}</option>
            </select>
            <Button
              // Sin la organización interna resuelta no se puede preseleccionar
              // nada, y la API rechaza una invitación sin organizaciones.
              disabled={!interna}
              onClick={() => {
                setInviteError(null);
                setInvitando(true);
              }}
            >
              {t("admin.invite")}
            </Button>
          </>
        }
      />

      <div className="px-8 py-6">
        {metrics.data && (
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label={t("admin.metricsActiveUsers")} value={metrics.data.activeUsers30d} />
            <StatCard
              label={t("admin.metricsPendingInvites")}
              value={metrics.data.pendingInvitations}
            />
            <StatCard label={t("admin.metricsPublished")} value={metrics.data.publishedStudies} />
            <StatCard label={t("admin.metricsViews")} value={metrics.data.viewsLast30d} />
          </div>
        )}

        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {users.isPending && <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>}
          {users.data && (
            <>
              <UsersTable
                users={users.data.items}
                currentUserId={userId}
                onEditUser={(usuario) => {
                  setErrorEdicion(null);
                  setUsuarioEnEdicion(usuario);
                }}
                onToggleStatus={(id, banned) => {
                  toggleStatus.mutate({ id, banned });
                }}
                onEditOrganizations={(usuario) => {
                  setMembresias(usuario.organizations.map((o) => o.id));
                  setErrorMembresias(null);
                  setEditando(usuario);
                }}
                onResendInvite={(usuario) => {
                  resendInvite.mutate(usuario);
                }}
              />
              <Pagination
                page={pagina}
                pageSize={PAGE_SIZE}
                total={users.data.total}
                onPageChange={setPagina}
              />
            </>
          )}
        </div>
      </div>

      {interna && (
        <InviteUserDialog
          open={invitando}
          organizations={opciones}
          internalOrganizationId={interna.id}
          error={inviteError}
          onOpenChange={setInvitando}
          onInvite={(payload) => void invitar(payload)}
        />
      )}

      {usuarioEnEdicion && (
        <EditUserDialog
          user={usuarioEnEdicion}
          currentUserId={userId}
          error={errorEdicion}
          onOpenChange={() => {
            setUsuarioEnEdicion(null);
          }}
          onSave={(payload) => {
            void guardarUsuario(usuarioEnEdicion, payload);
          }}
        />
      )}

      <Dialog
        open={editando !== null}
        onOpenChange={() => {
          setEditando(null);
        }}
        title={t("admin.editOrganizations")}
        {...optionalProp("description", editando?.email)}
      >
        <MultiSelect
          label={t("admin.organizations")}
          value={membresias}
          options={opciones}
          onChange={setMembresias}
          placeholder={t("admin.addOrganization")}
          removeLabel={(nombre) => t("admin.removeOrganization", { name: nombre })}
          emptyHint={t("admin.inviteNeedsOrganization")}
        />
        {/* El error NO va en `emptyHint`: ese <p> solo se pinta cuando no hay
            nada seleccionado, así que un fallo al guardar una lista no vacía
            se perdería. Va suelto, con role="alert" para que se anuncie. */}
        {errorMembresias && (
          <p role="alert" className="mt-2 text-micro font-bold text-warn">
            {errorMembresias}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setEditando(null);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            // Un usuario debe pertenecer al menos a una organización: la API
            // rechaza la lista vacía, así que aquí ni se manda.
            disabled={membresias.length === 0 || setOrganizations.isPending}
            onClick={() => {
              if (editando) void guardarMembresias(editando);
            }}
          >
            {t("common.save")}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
